import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { not } from 'ramda';
import { allColors } from '../../utils/styles';
import { SelectButton, ButtonLabel, Arrow, OptionItem, Label } from './styledComponents';
import './styles.scss';

export const OutlinedSelectContainer = styled.div`
	border: 2px solid #d6d6d6;
	border-radius: 8px;
	width: ${(props) => props.width || '100%'};
	height: 48px;
	position: relative;
	:hover {
		border-color: ${allColors.colorOrangeMain};
	}
`;

const SelectOptions = ({ options, onClickOption, currentValue }) => {
	return options.map((option) => (
		<OptionItem
			isActive={option.value === currentValue.value}
			isPlaceholder={option.placeholder}
			key={option.value}
			onClick={() => onClickOption(option)}
			name={option.value}
			role='option'
			className={`ng-binding ng-scope ${option.value === currentValue.value ? 'active' : ''}`}
			tabIndex='-1'
			aria-selected='true'>
			{option.textContent}
		</OptionItem>
	));
};

const MaterialSelect = ({
	optionsContainerStyles = {},
	selectWidth,
	selectOptions = [],
	onChange,
	placeholder,
	fontFamily = 'Calibri',
	name = '',
	initialValue,
	register,
	reset,
	disabled = false,
}) => {
	const placeholderItem = {
		textContent: placeholder,
		value: 'placeholder',
		shortContent: placeholder,
		placeholder: true,
	};
	const defaultOption = { value: '', textContent: '', shortContent: '' };

	const getDefaultValue = () => {
		if (!selectOptions || !selectOptions.length) return defaultOption;
		if (initialValue) return selectOptions.find((item) => item.value === initialValue);
		if (placeholder) return placeholderItem;
		return selectOptions[0];
	};
	
	const getOptions = () => {
		if (!selectOptions || !selectOptions.length) return [defaultOption];
		return placeholder ? [placeholderItem, ...selectOptions] : selectOptions;
	};

	const defaultValue = getDefaultValue();
	const optionsList = getOptions();

	const [activeSelect, setActiveSelect] = useState(false);
	const [selectValue, setSelectValue] = useState(defaultValue || {});

	const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef);

  function useOutsideAlerter(ref) {
    useEffect(() => {
      const handleClickOutside = event => ref.current && not(ref.current.contains(event.target)) && setActiveSelect(false);
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [ref]);
  }

	const validateDefaultState = () => {
		const inputObject = document.getElementById(name);
		if (inputObject) {
			const inputValue = inputObject.options.selectedIndex;
			if (Number(inputValue)) setSelectValue(optionsList[inputValue]);
		}
	};

	useEffect(validateDefaultState, []);

	useEffect(() => {
       if (reset) setSelectValue(defaultValue);
	}, [reset]);

	const handleSelect = () => {
		!disabled && setActiveSelect(!activeSelect);
	};

	const onClickOption = (newSelectValue) => {
		setSelectValue(newSelectValue);
		handleSelect();
	};

	useEffect(() => {
		const newDefaultOption = getDefaultValue();
		setSelectValue(newDefaultOption);
	}, [selectOptions]);

	useEffect(() => {
		if (onChange) onChange(selectValue.placeholder ? null : selectValue);
	}, [selectValue]);

	return (
		<div ref={wrapperRef} style={{ padding: 0, margin: 0, display: "flex" }}>
			<select
				style={{ display: 'none' }}
				onChange={() => {}}
				ref={register}
				name={name}
				id={name}
				value={selectValue.value}>
				{optionsList.map((optionObj) => (
					<option key={optionObj.textContent} value={optionObj.value}>
						{optionObj.shortContent || optionObj.textContent}
					</option>
				))}
			</select>
			<SelectButton width={selectWidth} onClick={handleSelect} type='button' className='select-box'>
				<ButtonLabel fontFamily={fontFamily} isPlaceholder={selectValue.placeholder}>
					<Label>{selectValue.shortContent || selectValue.textContent}</Label>
				</ButtonLabel>
				<Arrow>
					<span className={`arr ${activeSelect ? 'arr-up' : 'arr-down'}`}></span>
				</Arrow>
			</SelectButton>
			{selectOptions.length > 0 && (
				<div type='button' className={`md-select ${activeSelect ? 'active' : ''}`} style={optionsContainerStyles}>
					<ul role='listbox' className='option-list'>
						<SelectOptions options={optionsList} onClickOption={onClickOption} currentValue={selectValue} />
					</ul>
				</div>
			)}
		</div>
	);
};

export default MaterialSelect;
